/**
 * Renders the exact same resume preview DOM as the in-app Results step.
 * Used to capture that DOM with html2canvas so the downloaded PDF matches
 * the template view the user sees.
 */

import React from 'react';
import type { ResumeSection } from './utils/parseResumeSections';
import type { SavedProfileDesign } from './utils/savedProfileResumeFile';
import { SectionContentWithFormatting } from './utils/sectionContentRenderer';

const A4_PREVIEW_WIDTH = 794;

/**
 * Maps ALL 28 template IDs → their CSS skin class.
 * Must stay in sync with TEMPLATE_LIBRARY in ResultsStep.tsx.
 * Raw skin class strings are also accepted as pass-through for backwards compat.
 */
const TEMPLATE_ID_TO_SKIN: Record<string, string> = {
  // ── All 28 TEMPLATE_LIBRARY IDs ─────────────────────────────────────────
  'ats-optimized':     'blue-classic',
  'bold-dark':         'black-bold',
  'executive':         'black-clean',
  'navy-tech':         'navy-compact',
  'modern-blue':       'blue-modern',
  'developer':         'mono-clean',
  'slate-pro':         'slate-pro',
  'business-clean':    'blue-classic',
  'clean-minimal':     'blue-minimal',
  'creative-bold':     'black-clean',
  'marketing-pro':     'blue-modern',
  'finance-pro':       'navy-compact',
  'accounting':        'blue-classic',
  'healthcare-pro':    'blue-classic',
  'nursing':           'slate-pro',
  'health-admin':      'blue-modern',
  'fresh-grad':        'blue-minimal',
  'junior-tech':       'navy-compact',
  'entry-business':    'blue-classic',
  'classic-serif':     'black-serif',
  'legal-pro':         'black-clean',
  'research-scholar':  'black-serif',
  'faculty':           'mono-clean',
  'academic-clean':    'blue-classic',
  'soft-white':        'soft-white',
  'warm-cream':        'warm-cream',
  'sage-light':        'sage-light',
  'pearl-rose':        'pearl-rose',
  // ── Raw skin class pass-through (legacy / direct skin selection) ─────────
  'blue-classic':  'blue-classic',
  'blue-modern':   'blue-modern',
  'blue-minimal':  'blue-minimal',
  'navy-compact':  'navy-compact',
  'black-bold':    'black-bold',
  'black-clean':   'black-clean',
  'black-serif':   'black-serif',
  'mono-clean':    'mono-clean',
};
export function getTemplateSkinClass(id: string): string {
  return TEMPLATE_ID_TO_SKIN[id] ?? 'blue-classic';
}

const FIRST_SECTION_AS_PERSONAL = new Set([
  'resume', 'full resume', 'profile', 'professional summary', 'summary', 'objective',
  'contact', 'contact information', 'personal information',
]);
function isFirstSectionPersonalInfo(title: string): boolean {
  return FIRST_SECTION_AS_PERSONAL.has(title.toLowerCase().trim());
}

const ACCENT_PALETTE: Array<{
  swatch: string;
  accent: string;
  accentBg: string;
  accentText: string;
  accentBorder: string;
}> = [
  { swatch: '#94a3b8', accent: '#94a3b8', accentBg: '#f1f5f9', accentText: '#334155', accentBorder: '#cbd5e1' },
  { swatch: '#9ca3af', accent: '#9ca3af', accentBg: '#f3f4f6', accentText: '#374151', accentBorder: '#d1d5db' },
  { swatch: '#a8a29e', accent: '#a8a29e', accentBg: '#f5f5f4', accentText: '#44403c', accentBorder: '#d6d3d1' },
  { swatch: '#86b886', accent: '#6aaa6a', accentBg: '#f0f7f0', accentText: '#2d5a2d', accentBorder: '#b8d8b8' },
  { swatch: '#93c5fd', accent: '#60a5fa', accentBg: '#eff6ff', accentText: '#1e40af', accentBorder: '#bfdbfe' },
  { swatch: '#f9a8d4', accent: '#f472b6', accentBg: '#fdf2f8', accentText: '#9d174d', accentBorder: '#fbcfe8' },
];

function accentStyleFromDesign(design?: SavedProfileDesign): React.CSSProperties | undefined {
  if (!design?.color?.trim()) return undefined;
  const entry = ACCENT_PALETTE.find((p) => p.swatch === design.color);
  if (!entry) return undefined;
  return {
    '--resume-accent': entry.accent,
    '--resume-accent-bg': entry.accentBg,
    '--resume-accent-text': entry.accentText,
    '--resume-accent-border': entry.accentBorder,
  } as React.CSSProperties;
}

export interface ResumePreviewForPDFProps {
  sections: ResumeSection[];
  templateId: string;
  /** Section header label for the first (contact) section when applicable */
  personalInfoLabel?: string;
  /** Optional design tokens saved with the profile file (matches optimizer Results). */
  design?: SavedProfileDesign;
}

/**
 * Renders the same structure as ResultsStep's preview so html2canvas
 * produces a PDF that matches the in-app template view.
 */
const ResumePreviewForPDF: React.FC<ResumePreviewForPDFProps> = ({
  sections,
  templateId,
  personalInfoLabel = 'Personal Information',
  design,
}) => {
  const skinClass = getTemplateSkinClass(templateId);
  const fontClass =
    design?.font === 'serif'
      ? 'resume-preview--font-serif'
      : design?.font === 'compact'
        ? 'resume-preview--font-compact'
        : '';
  const caps = design?.caps === 'title' ? 'title' : design?.caps === 'upper' ? 'upper' : 'none';
  const capsClass = `resume-preview--caps-${caps}`;
  const customAccent = design?.color?.trim() ? 'resume-preview--custom-accent' : '';
  const accentStyle = accentStyleFromDesign(design);

  return (
    <div
      className={[
        'resume-preview',
        `resume-preview--${skinClass}`,
        customAccent,
        fontClass,
        capsClass,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        width: 794,
        maxWidth: 794,
        background: '#fff',
        boxSizing: 'border-box',
        ...accentStyle,
      }}
      data-template={templateId}
    >
      <div className="resume-preview-document-inner">
        {sections.map((section, i) => {
          const isContact = i === 0 && isFirstSectionPersonalInfo(section.title);
          const label =
            isContact
              ? personalInfoLabel
              : section.title === 'Full Resume'
                ? 'Full resume'
                : section.title;
          return (
            <div
              key={`section-${i}-${section.title}`}
              className="resume-preview-section-wrapper"
              style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
            >
              {/* Section header + first content line kept together — avoids the
                  dark header bar being orphaned at the bottom of a page */}
              <div
                style={{ pageBreakAfter: 'avoid', breakAfter: 'avoid' }}
              >
                <div
                  className={`resume-preview-section-card overflow-hidden${isContact ? ' resume-preview-contact-card' : ''}`}
                >
                  <div
                    className="resume-preview-section-header"
                    style={{ pageBreakAfter: 'avoid', breakAfter: 'avoid', pageBreakInside: 'avoid', breakInside: 'avoid' }}
                  >
                    <h4 style={{ margin: 0 }}>{label.toUpperCase()}</h4>
                  </div>
                  <div className="resume-preview-section-body">
                    <SectionContentWithFormatting
                      content={section.content || ''}
                      sectionTitle={section.title}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ResumePreviewForPDF;
